import { useState, useEffect } from "react";
import {
    ColumnMetadata,
    DataGrid,
    DataGridRow,
    RowSizes,
} from "~/components/DataGrid";
import { TableCard } from "~/components/TableCard";
import { ASSET_CLASS_IDS, TOKENIZATION_TYPE_IDS } from "~/consts";
import {
    ApiQuery,
    Filter,
    FilterOperators,
    PaginationQuery,
    SortDirections,
} from "~/modules/filters/types";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import {
    APY_7D_COLUMN,
    ASSET_CLASS_COLUMN,
    NAME_COLUMN,
    NETWORKS_COLUMN,
    PCT_CHANGE_30D_COLUMN,
    PCT_CHANGE_7D_COLUMN,
    PROTOCOL_COLUMN,
    RANK_COLUMN,
    TOKENIZATION_TYPE_COLUMN,
    VALUE_COLUMN,
} from "./columns";
import { GigatableColumns, GigatableRow } from "./types";
import { useInMemoryGigatableSort } from "./useInMemoryGigatableSort";
import { DistributionSelect } from "~/components/DistributionSelect";
import { DistributionType, DistributionTypes } from "~/types";
import { TableToggle } from "~/components/TableToggle";
import * as Analytics from "~/modules/main/analytics";
import { InfiniteScrollTracker } from "~/components/InfiniteScrollTracker";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { ExportTableNames } from "~/modules/downloads/types";
import { AssetColumns } from "~/modules/assets/types";

// // // //

const PAGE_SIZE = 50;

function GlobalAssetTable(props: { rows: GigatableRow[] }) {
    const allRows = props.rows;

    const { query, setQuery, sortedItems } = useInMemoryGigatableSort({
        rows: allRows,
        sort: {
            field: GigatableColumns.value,
            direction: SortDirections.desc,
        },
    });

    const [paginationQuery, setPaginationQuery] = useState<PaginationQuery>({
        page: 1,
        perPage: PAGE_SIZE,
    });

    // Reset pagination to page 1 when filters or sorting changes
    useEffect(() => {
        setPaginationQuery({ page: 1, perPage: PAGE_SIZE });
    }, [allRows, query.sort]);

    // For infinite scroll, show all items from the beginning up to current page
    const itemsToShow = paginationQuery.page * paginationQuery.perPage;
    const displayedRows = sortedItems.slice(0, itemsToShow);

    // Enable infinite scroll only if there are more items to load
    const hasMoreItems = itemsToShow < sortedItems.length;

    const columns: ColumnMetadata<GigatableRow, GigatableColumns>[] = [
        RANK_COLUMN,
        NAME_COLUMN,
        PROTOCOL_COLUMN,
        ASSET_CLASS_COLUMN,
        VALUE_COLUMN,
        PCT_CHANGE_7D_COLUMN,
        PCT_CHANGE_30D_COLUMN,
        APY_7D_COLUMN,
        NETWORKS_COLUMN,
        TOKENIZATION_TYPE_COLUMN,
    ];

    return (
        <>
            <DataGrid
                query={query}
                columns={columns}
                rows={displayedRows}
                rowKey="ticker"
                bordered={false}
                size={RowSizes.small}
                loading={false}
                fixedHeader={true}
                analyticsTableName="asset-gigatable"
                paginationState={{
                    page: 1,
                    pageCount: 1,
                    perPage: sortedItems.length,
                    resultCount: sortedItems.length,
                }}
                onChangeQuery={setQuery}
                enableItemsPerPageSelect={false}
                enableScrollButton={false}
                disablePagination={true}
                flush={true}
                showHeader={true}
                fixedHeaderClassName="top-24"
            >
                {({ row, rowIndex, scrollLeft }) => (
                    <DataGridRow<GigatableRow, GigatableColumns>
                        row={row}
                        key={row.url}
                        columns={columns}
                        size={RowSizes.small}
                        bordered={false}
                        scrollLeft={scrollLeft}
                        striped={rowIndex % 2 === 1}
                    />
                )}
            </DataGrid>

            {hasMoreItems && (
                <InfiniteScrollTracker
                    invisible={true}
                    onIntersect={() => {
                        setPaginationQuery((prev) => ({
                            ...prev,
                            page: prev.page + 1,
                        }));
                    }}
                />
            )}
        </>
    );
}

// // // //

export function GlobalAssetTableLayout(props: { rows: GigatableRow[] }) {
    const [includeStablecoins, setIncludeStablecoins] = useState(false);
    const [transferableFilter, setTransferableFilter] =
        useState<DistributionType>(DistributionTypes.Distributed);

    const rows = props.rows.filter((row) => {
        // Filter out stablecoins if not included
        if (
            !includeStablecoins &&
            row.asset_class.id === ASSET_CLASS_IDS.Stablecoin
        ) {
            return false;
        }

        // Filter based on transferability
        if (
            transferableFilter === DistributionTypes.Distributed &&
            !row.distributed
        ) {
            return false;
        }
        if (
            transferableFilter === DistributionTypes.Represented &&
            row.distributed
        ) {
            return false;
        }

        return true;
    });

    const distributedRows = props.rows.filter((row) => {
        if (
            !includeStablecoins &&
            row.asset_class.id === ASSET_CLASS_IDS.Stablecoin
        ) {
            return false;
        }
        return row.distributed;
    });

    const isolatedRows = props.rows.filter((row) => {
        if (
            !includeStablecoins &&
            row.asset_class.id === ASSET_CLASS_IDS.Stablecoin
        ) {
            return false;
        }
        return !row.distributed;
    });

    const allRows = props.rows.filter((row) => {
        if (
            !includeStablecoins &&
            row.asset_class.id === ASSET_CLASS_IDS.Stablecoin
        ) {
            return false;
        }
        return true;
    });

    // // // //
    // Construct filters for data export based on transferableFilter + includeStablecoins
    const filters: Filter[] = [];

    if (!includeStablecoins) {
        filters.push({
            operator: FilterOperators.NotEquals,
            field: AssetColumns.assetClassID,
            value: ASSET_CLASS_IDS.Stablecoin,
        });
    }

    if (transferableFilter === DistributionTypes.Distributed) {
        filters.push({
            operator: FilterOperators.Includes,
            field: AssetColumns.tokenizationTypeIDs,
            value: [TOKENIZATION_TYPE_IDS.OnChainDistributed],
        });
        filters.push({
            operator: FilterOperators.NotIncludes,
            field: AssetColumns.tokenizationTypeIDs,
            value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
        });
    }
    if (transferableFilter === DistributionTypes.Represented) {
        filters.push({
            operator: FilterOperators.Includes,
            field: AssetColumns.tokenizationTypeIDs,
            value: [TOKENIZATION_TYPE_IDS.OnChainRepresented],
        });
    }

    // // // //

    return (
        <VisualizationGrid>
            <TableCard
                title="All Assets"
                button={
                    <div className="flex flex-col gap-2 items-start lg:flex-row lg:gap-8 lg:items-center">
                        <DistributionSelect
                            value={transferableFilter}
                            onChange={(value) => {
                                setTransferableFilter(value);

                                Analytics.trackGlobalAssetTableChangeDistribution(
                                    {
                                        distributionType: value,
                                    },
                                );
                            }}
                            counts={{
                                distributed: distributedRows.length,
                                represented: isolatedRows.length,
                                all: allRows.length,
                            }}
                            includeDescription={false}
                            includeTooltips={true}
                        />

                        <TableToggle
                            checked={includeStablecoins}
                            onChange={setIncludeStablecoins}
                            label="Include Stablecoins"
                        />

                        {/* NOTE - only for enterprise users now use right now (not even shown to non-enterprise users) */}
                        <FeatureFlag feature={FeatureNames.enableApiKeys}>
                            <DataExportProvider>
                                {({ isLoading, exportCsv }) => (
                                    <DownloadButton
                                        disabled={isLoading}
                                        onClick={() => {
                                            // Set filename for export based on filters applied
                                            let exportFilename =
                                                transferableFilter ===
                                                DistributionTypes.Distributed
                                                    ? "distributed-assets"
                                                    : transferableFilter ===
                                                        DistributionTypes.Represented
                                                      ? "represented-assets"
                                                      : "all-assets";

                                            if (includeStablecoins) {
                                                exportFilename +=
                                                    "-with-stablecoins";
                                            } else {
                                                exportFilename +=
                                                    "-without-stablecoins";
                                            }

                                            // Construct query for data export
                                            const query: ApiQuery = {
                                                filter: {
                                                    operator:
                                                        FilterOperators.And,
                                                    filters,
                                                },
                                                pagination: {
                                                    page: 1,
                                                    perPage: 10000,
                                                },
                                                sort: {
                                                    field: GigatableColumns.name,
                                                    direction:
                                                        SortDirections.asc,
                                                },
                                            };

                                            // Export the CSV data
                                            exportCsv({
                                                query,
                                                exportFilename,
                                                exportTableName:
                                                    ExportTableNames.assets_v4,
                                            });
                                        }}
                                    />
                                )}
                            </DataExportProvider>
                        </FeatureFlag>
                    </div>
                }
                colSpans={{
                    sm: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                    xxl: 12,
                }}
            >
                <GlobalAssetTable rows={rows} />
            </TableCard>
        </VisualizationGrid>
    );
}
